import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

const Card = styled.div`
  background-color: white;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  padding: 24px;
  transition: all 0.2s ease;
  cursor: pointer;

  &:hover {
    border-color: #22c55e;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
`;

const ProductName = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin: 0 0 8px 0;
`;

const ProductDescription = styled.p`
  font-size: 14px;
  color: #666;
  margin: 0 0 16px 0;
  min-height: 40px;
`;

const PriceSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 0;
  border-top: 1px solid #e5e7eb;
  border-bottom: 1px solid #e5e7eb;
  margin-bottom: 16px;
`;

const PriceLabel = styled.div`
  font-size: 14px;
  color: #666;
`;

const PriceValue = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: #333;
`;

const ValueSection = styled.div`
  background-color: #ecfdf5;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
`;

const ValueRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;

  &:last-child {
    margin-bottom: 0;
    padding-top: 8px;
    border-top: 1px dashed #86efac;
    font-weight: 600;
  }
`;

const ValueLabel = styled.span`
  font-size: 14px;
  color: #166534;
`;

const ValueAmount = styled.span`
  font-size: 14px;
  color: #166534;
  font-weight: 600;
`;

const BonusBadge = styled.span`
  background-color: #fbbf24;
  color: #78350f;
  font-size: 11px;
  font-weight: 600;
  padding: 2px 6px;
  border-radius: 4px;
  margin-left: 8px;
`;

const ExpirationNotice = styled.div`
  font-size: 12px;
  color: #92400e;
  background-color: #fef3c7;
  padding: 8px 12px;
  border-radius: 6px;
  margin-bottom: 16px;
`;

const SelectButton = styled.button`
  width: 100%;
  padding: 12px 20px;
  background-color: #22c55e;
  color: white;
  font-size: 15px;
  font-weight: 600;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: #16a34a;
  }
`;

const SavingsTag = styled.div`
  position: absolute;
  top: -10px;
  right: 16px;
  background-color: #ef4444;
  color: white;
  font-size: 12px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 4px;
`;

const CardWrapper = styled.div`
  position: relative;
`;

const VoucherProductCard = ({ product, onSelect }) => {
  const hasPromo = product.promo_value_cents > 0;
  const savings = product.total_value_cents - product.purchase_price_cents;
  const hasSavings = savings > 0;

  // Format savings as currency
  const formatCurrency = (cents) => `$${(cents / 100).toFixed(2)}`;

  return (
    <CardWrapper>
      <Card onClick={() => onSelect(product)}>
        {hasSavings && <SavingsTag>Save {formatCurrency(savings)}</SavingsTag>}

        <ProductName>{product.name}</ProductName>
        <ProductDescription>
          {product.description || 'Purchase credit to use on your orders.'}
        </ProductDescription>

        <PriceSection>
          <PriceLabel>You Pay</PriceLabel>
          <PriceValue>{product.purchase_price_formatted}</PriceValue>
        </PriceSection>

        <ValueSection>
          <ValueRow>
            <ValueLabel>Purchased Credit</ValueLabel>
            <ValueAmount>
              {formatCurrency(product.purchased_value_cents)}
            </ValueAmount>
          </ValueRow>
          {hasPromo && (
            <ValueRow>
              <ValueLabel>
                Bonus Credit
                <BonusBadge>FREE</BonusBadge>
              </ValueLabel>
              <ValueAmount>
                {formatCurrency(product.promo_value_cents)}
              </ValueAmount>
            </ValueRow>
          )}
          <ValueRow>
            <ValueLabel>Total to Spend</ValueLabel>
            <ValueAmount style={{ fontSize: '18px' }}>
              {product.total_value_formatted}
            </ValueAmount>
          </ValueRow>
        </ValueSection>

        {hasPromo && product.promo_expires && (
          <ExpirationNotice>
            Bonus credit expires {product.promo_expiration_days} days after
            purchase. Purchased credit never expires.
          </ExpirationNotice>
        )}

        <SelectButton>Select This Voucher</SelectButton>
      </Card>
    </CardWrapper>
  );
};

VoucherProductCard.propTypes = {
  product: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    description: PropTypes.string,
    purchase_price_cents: PropTypes.number.isRequired,
    purchase_price_formatted: PropTypes.string.isRequired,
    purchased_value_cents: PropTypes.number.isRequired,
    promo_value_cents: PropTypes.number.isRequired,
    total_value_cents: PropTypes.number.isRequired,
    total_value_formatted: PropTypes.string.isRequired,
    promo_expires: PropTypes.bool,
    promo_expiration_days: PropTypes.number,
  }).isRequired,
  onSelect: PropTypes.func.isRequired,
};

export default VoucherProductCard;
